// /admin/alumnos-inactivos — alumnos con bono activo que llevan ≥N días sin asistir.
//
// Caso de uso: identificar candidatos para email de cuidado o llamada
// proactiva — pagaron, todavía tienen créditos, pero llevan tiempo sin
// venir. Excluye membresías (no aplica el contador de créditos sin usar).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserMinus, Loader2, AlertCircle, Mail } from 'lucide-react';

import { getAlumnosInactivos } from '../../lib/adminApi';

function localePara(lang) {
  const map = { es: 'es-ES', en: 'en-GB', ru: 'ru-RU', fr: 'fr-FR' };
  return map[lang] || 'es-ES';
}

function fmtFecha(iso, lang) {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return new Intl.DateTimeFormat(localePara(lang), {
    day: 'numeric', month: 'short', year: 'numeric',
  }).format(d);
}

function diasDesde(iso) {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  return Math.round((Date.now() - d.getTime()) / 86400000);
}

function diasHasta(iso) {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  return Math.round((d.getTime() - Date.now()) / 86400000);
}

const RANGOS = [7, 14, 21, 30, 45, 60, 90];

export default function AdminAlumnosInactivos() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || 'es').toLowerCase();
  const [dias, setDias] = useState(14);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAlumnosInactivos({ dias_inactividad: dias, limit: 100 })
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dias]);

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1 className="admin-page-titulo">
            <UserMinus size={22} strokeWidth={1.5} /> {t('admin.alumnos_inactivos.titulo')}
          </h1>
          <p className="admin-page-sub">{t('admin.alumnos_inactivos.sub')}</p>
        </div>
        <div className="admin-filtros-inline">
          <label className="admin-label">
            {t('admin.alumnos_inactivos.rango')}{' '}
            <select value={dias} onChange={(e) => setDias(parseInt(e.target.value, 10))}>
              {RANGOS.map((d) => (
                <option key={d} value={d}>{t('admin.alumnos_inactivos.dias', { dias: d })}</option>
              ))}
            </select>
          </label>
        </div>
      </header>

      {loading && (
        <div className="admin-loading"><Loader2 size={20} className="spin" /> <span>{t('admin.cargando')}</span></div>
      )}
      {error && (
        <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>
      )}
      {!loading && !error && data && data.data.length === 0 && (
        <div className="admin-empty">{t('admin.alumnos_inactivos.vacio', { dias })}</div>
      )}
      {!loading && !error && data && data.data.length > 0 && (
        <div className="admin-card">
          <table className="admin-tabla">
            <thead>
              <tr>
                <th>#</th>
                <th>{t('admin.alumnos_inactivos.col_alumno')}</th>
                <th>{t('admin.alumnos_inactivos.col_email')}</th>
                <th className="admin-col-num">{t('admin.alumnos_inactivos.col_creditos')}</th>
                <th className="admin-col-num">{t('admin.alumnos_inactivos.col_bonos')}</th>
                <th>{t('admin.alumnos_inactivos.col_caducidad')}</th>
                <th>{t('admin.alumnos_inactivos.col_ultima')}</th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((a, i) => {
                const dDesde = diasDesde(a.ultima_clase);
                const dHasta = diasHasta(a.caducidad_proxima);
                const caducaPronto = dHasta !== null && dHasta <= 7;
                const nombre = [a.nombre, a.apellidos].filter(Boolean).join(' ').trim()
                  || a.email
                  || t('admin.alumnos_inactivos.sin_nombre');
                return (
                  <tr key={a.usuario_id || i}>
                    <td className="admin-col-num">{i + 1}</td>
                    <td>{nombre}</td>
                    <td className="admin-celda-tenue">
                      {a.email
                        ? (
                          <a href={`mailto:${a.email}`} className="admin-email-link">
                            <Mail size={14} strokeWidth={1.5} />
                            <span>{a.email}</span>
                          </a>
                        )
                        : '—'}
                    </td>
                    <td className="admin-col-num"><strong>{a.creditos_restantes_total}</strong></td>
                    <td className="admin-col-num">{a.bonos_activos}</td>
                    <td>
                      {a.caducidad_proxima ? (
                        <>
                          <span>{fmtFecha(a.caducidad_proxima, lang)}</span>
                          {dHasta !== null && (
                            <span className={'admin-cell-meta' + (caducaPronto ? ' admin-cell-meta--clay' : '')}>
                              {' · '}{caducaPronto
                                ? t('admin.alumnos_inactivos.caduca_en', { dias: Math.max(dHasta, 0) })
                                : t('admin.alumnos_inactivos.dias_para_caducar', { dias: dHasta })}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="admin-celda-tenue">{t('admin.alumnos_inactivos.sin_caducidad')}</span>
                      )}
                    </td>
                    <td>
                      {a.ultima_clase ? (
                        <>
                          <span>{fmtFecha(a.ultima_clase, lang)}</span>
                          {dDesde !== null && (
                            <span className="admin-cell-meta admin-cell-meta--clay">
                              {' · '}{t('admin.alumnos_inactivos.hace_dias', { count: dDesde })}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="admin-celda-tenue">{t('admin.alumnos_inactivos.jamas')}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
